import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Badge, Box, Button, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { RenderingContainerType } from 'constants/telephony';

import { setTelephonyActiveTabAction } from 'store/system/actions';
import {
  selectBorrowerChatUnreadCount,
  selectInternalChatUnreadCount,
  selectTelephonyActiveTab,
  selectVoicemailUnreadCount,
} from 'store/system/selectors';

import { ACTIVE_TAB_STORAGE_KEY } from '../../TelephonyWidget.constants';

import {
  activeIndicatorStyles,
  tabBarContainerStyles,
  tabButtonStyles,
  tabLabelStyles,
} from './TelephonyTabBar.styles';
import { TelephonyTabBarTestIds } from './TelephonyTabBar.testIds';

const TelephonyTabBar = ({ tabs }) => {
  const dispatch = useDispatch();
  const activeTab = useSelector(selectTelephonyActiveTab);
  const borrowerChatUnread = useSelector(selectBorrowerChatUnreadCount);
  const internalChatUnread = useSelector(selectInternalChatUnreadCount);
  const voicemailUnread = useSelector(selectVoicemailUnreadCount);

  const onTabClick = useCallback(
    e => {
      const { tabKey } = e.currentTarget.dataset;

      dispatch(setTelephonyActiveTabAction(tabKey));

      try {
        localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tabKey);
      } catch {
        // localStorage unavailable
      }
    },
    [dispatch],
  );

  return (
    <Box role="tablist" sx={tabBarContainerStyles}>
      {tabs.map(({ key, label, Icon }) => (
        <Button
          disableElevation
          disableRipple
          aria-selected={activeTab === key}
          color="inherit"
          data-is-active={activeTab === key}
          data-tab-key={key}
          data-testid={TelephonyTabBarTestIds.getTab(key)}
          key={key}
          role="tab"
          sx={tabButtonStyles}
          variant="text"
          onClick={onTabClick}
        >
          <Badge
            color="error"
            invisible={
              !(key === RenderingContainerType.CHAT && borrowerChatUnread > 0) &&
              !(key === RenderingContainerType.INTERNAL_CHAT && internalChatUnread > 0) &&
              !(key === RenderingContainerType.VOICEMAILS && voicemailUnread > 0)
            }
            overlap="circular"
            variant="dot"
          >
            <Icon size={IconSize[20]} />
          </Badge>

          <Typography sx={tabLabelStyles} variant="regular12">
            {label}
          </Typography>

          {activeTab === key && <Box sx={activeIndicatorStyles} />}
        </Button>
      ))}
    </Box>
  );
};

TelephonyTabBar.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      Icon: PropTypes.elementType.isRequired,
      hasBadge: PropTypes.bool,
    }),
  ).isRequired,
};

export default memo(TelephonyTabBar);
